import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Loader2, Mail, Phone, MapPin } from "lucide-react";
import Seo from "../components/site/Seo";
import { PageHeader, SectionLabel } from "../components/site/Section";
import { Reveal } from "../components/site/Reveal";
import { submitContact } from "../lib/api";
import { CONTACT } from "../data/site";

const FIELD = "w-full border border-border bg-secondary px-4 py-3 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-brand";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", inquiryType: "Client", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please complete the required fields.");
    setLoading(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success("Message sent. We'll respond shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const details = [
    { icon: Mail, label: "Contracts & Clients", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: Mail, label: "Careers & Candidates", value: CONTACT.careersEmail, href: `mailto:${CONTACT.careersEmail}` },
    { icon: Phone, label: "Phone", value: CONTACT.phone, href: `tel:${CONTACT.phone}` },
    { icon: MapPin, label: "Coverage", value: CONTACT.address, href: null },
  ];

  return (
    <div data-testid="contact-page">
      <Seo title="Contact" description="Contact VectorBridge Group LLC to discuss staffing, government contracting, and workforce solutions." />
      <PageHeader label="K/01" kicker="Contact" title="Let's talk about your workforce." intro="Whether you're staffing a contract, building a team, or exploring your next role — start the conversation here." />

      <section className="border-b border-border py-16 sm:py-24">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 sm:px-8 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionLabel index="K/02">Direct lines</SectionLabel>
            <div className="mt-8 divide-y divide-border border-t border-border">
              {details.map((d, i) => {
                const Icon = d.icon;
                const inner = (
                  <div className="flex items-start gap-4 py-6">
                    <Icon size={20} className="mt-0.5 shrink-0 text-brand" />
                    <div>
                      <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{d.label}</div>
                      <div className="mt-1">{d.value}</div>
                    </div>
                  </div>
                );
                return (
                  <Reveal key={d.label} delay={i * 0.05}>
                    {d.href ? <a href={d.href} className="block transition-colors hover:text-brand">{inner}</a> : inner}
                  </Reveal>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-8">
            {done ? (
              <div data-testid="contact-success" className="border border-border bg-secondary p-12 text-center">
                <CheckCircle2 size={40} className="mx-auto text-brand" />
                <h3 className="mt-6 font-display text-3xl tracking-tight">Message received.</h3>
                <p className="mt-3 text-muted-foreground">Thank you, {form.name.split(" ")[0]}. A member of our team will respond within one business day.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="border border-border p-6 sm:p-10">
                <SectionLabel index="K/03">Send a message</SectionLabel>
                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  <input name="name" value={form.name} onChange={change} placeholder="Full name *" className={FIELD} data-testid="contact-name" />
                  <input name="email" type="email" value={form.email} onChange={change} placeholder="Email *" className={FIELD} data-testid="contact-email" />
                  <input name="organization" value={form.organization} onChange={change} placeholder="Organization" className={FIELD} data-testid="contact-organization" />
                  <select name="inquiryType" value={form.inquiryType} onChange={change} className={FIELD} data-testid="contact-type">
                    <option>Client</option>
                    <option>Candidate</option>
                    <option>Government / Contracting</option>
                    <option>General</option>
                  </select>
                  <textarea name="message" value={form.message} onChange={change} rows={6} placeholder="How can we help? *" className={`${FIELD} sm:col-span-2`} data-testid="contact-message" />
                </div>
                <button type="submit" disabled={loading} data-testid="contact-submit" className="group mt-6 inline-flex items-center gap-2 bg-brand px-7 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy disabled:opacity-60">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
                  {loading ? "Sending" : "Send message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
